import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import {
  ActionPerformed,
  PushNotifications,
  Token,
} from "@capacitor/push-notifications";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { catchError, from, map, Observable, of, switchMap } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { ResponseModel } from "@modules/auth/_models/response.model";
import { OpenSubjectService } from "@services/OpenSubjectService/open-subject.service";
import { AppStateService } from "src/app/store/app-state.service";

const API_USERS_URL = `${environment.apiUrl}`;
const REGISTER_ACTION = "register";
const DEREGISTER_ACTION = "deregister";

@Injectable({ providedIn: "root" })
export class PushNotificationService {
  private readonly isPushAvailable =
    Capacitor.isPluginAvailable("PushNotifications");
  private readonly isFirebaseAvailable =
    Capacitor.isPluginAvailable("FirebaseMessaging");
  private listenersRegistered = false;
  private firebaseListenersRegistered = false;
  private isRegistered = false;
  private currentFcmToken?: string;
  private pendingNotificationData?: Record<string, any>;
  private pendingDeepLinkRetryCount = 0;
  private deepLinkRetryTimeout?: ReturnType<typeof setTimeout>;
  private readonly maxDeepLinkRetries = 30;
  private readonly deepLinkRetryDelayMs = 300;
  private fcmTokenRetryTimeout?: ReturnType<typeof setTimeout>;
  private readonly maxFcmTokenRetries = 10;
  private readonly fcmTokenRetryDelayMs = 1000;

  constructor(
    private http: HttpClient,
    private openSubject: OpenSubjectService,
    private appStateService: AppStateService
  ) {
    // Register listeners eagerly so a cold start from notification tap does not lose the action event.
    this.registerListeners();
  }

  async register(): Promise<void> {
    if (!this.isPushAvailable || this.isRegistered) {
      return;
    }
    const hasPermission = await this.ensurePermissions();
    if (!hasPermission) {
      console.log("[push] Permission denied, skipping push registration");
      return;
    }
    this.registerListeners();
    if (this.isFirebaseAvailable) {
      this.initFirebaseListeners();
    }
    await PushNotifications.register();
    this.trySyncFirebaseToken();
  }

  deregister(): void {
    if (!this.currentFcmToken) {
      return;
    }
    this.sendFcmToken(this.currentFcmToken, DEREGISTER_ACTION).subscribe(() => {
      this.currentFcmToken = undefined;
      this.isRegistered = false;
    });
    PushNotifications.removeAllListeners();
    FirebaseMessaging.removeAllListeners();
    this.listenersRegistered = false;
    this.firebaseListenersRegistered = false;
    this.pendingNotificationData = undefined;
    this.pendingDeepLinkRetryCount = 0;
    if (this.deepLinkRetryTimeout) {
      clearTimeout(this.deepLinkRetryTimeout);
      this.deepLinkRetryTimeout = undefined;
    }
    if (this.fcmTokenRetryTimeout) {
      clearTimeout(this.fcmTokenRetryTimeout);
      this.fcmTokenRetryTimeout = undefined;
    }
  }

  /* ================= PERMISSIONS ================= */
  async requestPermission(): Promise<boolean> {
    if (!this.isPushAvailable || this.isRegistered) {
      return false;
    }
    let perms = await PushNotifications.checkPermissions();
    if (perms.receive === "prompt") {
      perms = await PushNotifications.requestPermissions();
    }
    return perms.receive === "granted";
  }

  private async ensurePermissions(): Promise<boolean> {
    let perms = await PushNotifications.checkPermissions();
    if (perms.receive === "prompt") {
      perms = await PushNotifications.requestPermissions();
    }
    return perms.receive === "granted";
  }

  /* ================= LISTENERS ================= */
  private registerListeners(): void {
    if (this.listenersRegistered || !this.isPushAvailable) {
      return;
    }
    this.listenersRegistered = true;

    PushNotifications.addListener("registration", (token: Token) => {
      console.log("[push] Native registration completed", token?.value);
      if (Capacitor.getPlatform() !== "ios" && token?.value) {
        this.registerFcmToken(token.value);
      }

      if (this.isFirebaseAvailable) {
        this.initFirebaseListeners();
        this.trySyncFirebaseToken();
      }
    });

    PushNotifications.addListener("registrationError", (err) =>
      console.error("[push] registrationError", err)
    );

    PushNotifications.addListener("pushNotificationReceived", (n) =>
      console.log("[push] received", n)
    );

    PushNotifications.addListener("pushNotificationActionPerformed", (n) =>
      this.handleNotificationTap(n)
    );

    if (this.isFirebaseAvailable) {
      FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
        const data = (event?.notification?.data ?? {}) as Record<string, any>;
        this.handleNotificationData(data);
      });
    }
  }

  /* ================= FIREBASE ================= */
  private initFirebaseListeners(): void {
    if (this.firebaseListenersRegistered) {
      return;
    }
    this.firebaseListenersRegistered = true;

    FirebaseMessaging.addListener("tokenReceived", (token) => {
      console.log("[push] FCM token received", token.token);
      this.registerFcmToken(token.token);
    });

    FirebaseMessaging.addListener("notificationReceived", (n) =>
      console.log("[push] FCM notification received", n)
    );
  }

  private trySyncFirebaseToken(retry = 0): void {
    if (!this.isFirebaseAvailable || this.isRegistered) {
      return;
    }

    FirebaseMessaging.getToken()
      .then((result) => {
        const token = result?.token;
        if (token) {
          this.registerFcmToken(token);
          return;
        }
        this.scheduleFcmTokenRetry(retry);
      })
      .catch(() => this.scheduleFcmTokenRetry(retry));
  }

  private scheduleFcmTokenRetry(retry: number): void {
    if (retry >= this.maxFcmTokenRetries || this.isRegistered) {
      return;
    }

    if (this.fcmTokenRetryTimeout) {
      clearTimeout(this.fcmTokenRetryTimeout);
    }

    const nextRetry = retry + 1;
    this.fcmTokenRetryTimeout = setTimeout(
      () => this.trySyncFirebaseToken(nextRetry),
      this.fcmTokenRetryDelayMs
    );
  }

  /* ================= REGISTRATION ================= */
  private registerFcmToken(token: string): void {
    if (this.currentFcmToken === token && this.isRegistered) {
      return;
    }
    this.currentFcmToken = token;
    this.sendFcmToken(token, REGISTER_ACTION).subscribe((registered) => {
      if (registered) {
        this.isRegistered = true;
        console.log("[push] FCM token registered");
        return;
      }

      this.isRegistered = false;
      console.error("[push] backend register-fcm failed, retrying token sync");
      this.trySyncFirebaseToken();
    });
  }

  private sendFcmToken(
    token: string,
    action: "register" | "deregister"
  ): Observable<boolean> {
    return from(Device.getId()).pipe(
      switchMap(() => {
        const payload = {
          token,
          deviceType: Capacitor.getPlatform(), // matches DeviceFcmInfo
        };
        console.log(`[push] ${action}`, payload);
        return this.http
          .post<ResponseModel<boolean>>(
            `${API_USERS_URL}/v1/Notification/${action}-fcm`,
            payload
          )
          .pipe(
            map((r) => r.succeeded),
            catchError(() => of(false))
          );
      })
    );
  }

  /* ================= NOTIFICATION TAP ================= */
  private handleNotificationTap(event: ActionPerformed): void {
    const data = (event.notification.data ?? {}) as Record<string, any>;
    this.handleNotificationData(data);
  }

  private handleNotificationData(data: Record<string, any>): void {
    console.log("[push] Notification tap received:", JSON.stringify(data));
    this.pendingNotificationData = data;
    this.pendingDeepLinkRetryCount = 0;
    this.tryProcessPendingDeepLink();
  }

  private tryProcessPendingDeepLink(): void {
    if (!this.pendingNotificationData) {
      return;
    }

    if (!this.appStateService.state.currentUser?.id) {
      this.scheduleDeepLinkRetry();
      return;
    }

    const data = this.pendingNotificationData;

    if (data.PostId) {
      const postId = Number(data.PostId);
      const replyId = data.ReplyId ? Number(data.ReplyId) : undefined;
      console.log("[push] Opening post:", postId, "reply:", replyId);
      this.openSubject.openPost(postId, replyId);
    } else if (data.GroupId) {
      const groupId = Number(data.GroupId);
      console.log("[push] Opening group:", groupId);
      this.openSubject.openGroup(groupId);
    } else if (data.UserProfileId) {
      console.log("[push] Opening chat with user:", data.UserProfileId);
      this.openSubject.openChat(data.UserProfileId);
    } else {
      console.warn("[push] Unknown notification type:", data);
    }

    this.pendingNotificationData = undefined;
    this.pendingDeepLinkRetryCount = 0;
    if (this.deepLinkRetryTimeout) {
      clearTimeout(this.deepLinkRetryTimeout);
      this.deepLinkRetryTimeout = undefined;
    }
  }

  private scheduleDeepLinkRetry(): void {
    if (this.pendingDeepLinkRetryCount >= this.maxDeepLinkRetries) {
      console.warn(
        "[push] Deep link processing timed out waiting for authenticated app state"
      );
      return;
    }

    if (this.deepLinkRetryTimeout) {
      clearTimeout(this.deepLinkRetryTimeout);
    }

    this.pendingDeepLinkRetryCount += 1;
    this.deepLinkRetryTimeout = setTimeout(
      () => this.tryProcessPendingDeepLink(),
      this.deepLinkRetryDelayMs
    );
  }
}
